import { PanelToggleButton } from "@lazarentertainment/web-design-system/components/panelToggleButton"
import { Spinner } from "@lazarentertainment/web-design-system/components/spinner"
import AnimationTarget from "client/packages/animations/AnimationTarget"
import { CommerceMobileSheet } from "client/packages/commerce/components/CommerceMobileSheet"
import { GiveawaysMobileSheet } from "client/packages/giveaways/components/GiveawaysMobileSheet/GiveawaysMobileSheet"
import { DesktopVideoOverlayLayer } from "client/packages/player/player/DesktopVideoOverlayLayer"
import { MobileVideoOverlayLayer } from "client/packages/player/player/MobileVideoOverlayLayer"
import { UnmuteButton } from "client/packages/player/player/UnmuteButton"
import { useVideoContext } from "client/packages/player/player/VideoContext"
import { PlayerErrorMessage } from "client/packages/player/ui/PlayerErrorMessage"
import VideoPlayer from "client/packages/player/VideoPlayer"
import VodVideoPlayer from "client/packages/player/vod/VodVideoPlayer"
import { TargetMobileSheet } from "client/packages/target/components/TargetMobileSheet"
import { useGlobalContext } from "client/packages/utils/GlobalContext"
import { VotingMobileSheet } from "client/packages/voting/components/VotingMobileSheet"
import { useRef } from "react"
import { RozyLayout } from "shared/constants/views"

import { useChannelContext } from "../channel"
import { useLayoutContext } from "../layout"
import { FullscreenProvider } from "../layout/FullscreenContext"

import { SidePanelContent } from "./SidePanelContent"
import SplashImage from "./SplashImage"

import styles from "./mainView.module.css"

export type MainViewProps = {
  /**
   * In previewMode, the MainView shows a simplified interface,
   * enticing the user to click and open itself in to a full-page lightbox view,
   * where previewMode=false and all of the contents (chat, shopping, video controls) are shown.
   */
  previewMode?: boolean
}

export const MainView = ({ previewMode = false }) => {
  const { recordingId, aspectRatio, live, watermarkOnPlayer } =
    useChannelContext()
  const { setLightboxContent } = useGlobalContext()
  const { layout, orientation } = useLayoutContext()
  const { playerError, isPlaying, canPlay, mutedPreview } =
    useVideoContext()

  const {
    state: {
      isSidePanelOpen,
      isCommercePluginAvailable,
      isTargetPluginAvailable,
      isVotingPluginAvailable,
      isGiveawaysPluginAvailable,
    },
    setSidePanelOpen,
  } = useGlobalContext()

  const fullscreenableDiv = useRef<HTMLDivElement>(null)

  // On Firefox, the video starts playing before canPlay becomes true. We hide the spinner in that case.
  const shouldShowLoadingSpinner =
    live && !playerError && !canPlay && !isPlaying

  const shouldShowUnmuteButton =
    live && !playerError && !shouldShowLoadingSpinner && mutedPreview

  return (
    <FullscreenProvider fullscreenElement={fullscreenableDiv}>
      <div className={styles.mainView} ref={fullscreenableDiv}>
        <div
          className={styles.videoArea}
          data-layout={layout}
          data-preview-mode={previewMode}
          data-orientation={orientation}
          data-video-aspect-ratio={aspectRatio}
        >
          {recordingId ? (
            <VodVideoPlayer
              mobileSubtitles={layout === RozyLayout.MOBILE}
            />
          ) : (
            <VideoPlayer mobileSubtitles={layout === RozyLayout.MOBILE} />
          )}
          <SplashImage />
          {playerError && <PlayerErrorMessage error={playerError} />}
          {shouldShowLoadingSpinner && (
            <div className={styles.spinnerContainer}>
              <Spinner data-size="3xl" />
            </div>
          )}
          {shouldShowUnmuteButton && (
            <UnmuteButton
              onClick={
                previewMode
                  ? () => {
                      setLightboxContent({ contentType: "live" })
                    }
                  : undefined
              }
            />
          )}
          <AnimationTarget />
          {!previewMode && !playerError && (
            <>
              {layout === RozyLayout.DESKTOP ? (
                <DesktopVideoOverlayLayer
                  brandWatermark={watermarkOnPlayer}
                />
              ) : (
                <MobileVideoOverlayLayer
                  brandWatermark={watermarkOnPlayer}
                />
              )}
            </>
          )}
          {!previewMode && (
            <>
              {isCommercePluginAvailable && <CommerceMobileSheet />}
              {isTargetPluginAvailable && <TargetMobileSheet />}
              {isVotingPluginAvailable && <VotingMobileSheet />}
              {isGiveawaysPluginAvailable && <GiveawaysMobileSheet />}
              {layout !== RozyLayout.MOBILE && (
                <div className={styles.sidePanelToggleButtonWrapper}>
                  <PanelToggleButton
                    direction="right"
                    panelVisible={isSidePanelOpen}
                    onClick={() => {
                      setSidePanelOpen(!isSidePanelOpen)
                    }}
                  />
                </div>
              )}
            </>
          )}
        </div>
        {!previewMode && layout !== RozyLayout.MOBILE && (
          <div className={styles.sidePanel} data-open={isSidePanelOpen}>
            <SidePanelContent />
          </div>
        )}
      </div>
    </FullscreenProvider>
  )
}

export default MainView
